import { XirrData } from "@/utils/types/UserXirr";
import {
  Percent,
  Download,
  ArrowDownRight,
  Calendar,
  Wallet,
  IndianRupee,
  Info,
  PersonStanding,
} from "lucide-react";

interface Props {
  data: XirrData | null;
}

const XirrPerformance = ({ data }: Props) => {
  return (
    <div className="card mb-6 rounded-xl border bg-card p-4 shadow-sm">
      {/* HEADER */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-secondary">
            <Percent className="h-5 w-5 text-primary" />
            Investment Performance (XIRR)
          </h2>
          <p className="text-sm text-muted-foreground">
            Returns calculated using Extended Internal Rate of Return (XIRR)
            method
          </p>
        </div>

        <button className="flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm transition hover:bg-muted">
          <Download className="h-4 w-4" />
          Download Statement
        </button>
      </div>

      {/* GRID */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-2 lg:grid-cols-5">
        {/* Overall XIRR */}
        <div className="rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 p-4">
          <div className="mb-2 flex items-center gap-2">
            <Percent className="h-4 w-4 text-primary" />
            <p className="text-xs text-muted-foreground">Overall XIRR</p>
          </div>

          <p className="text-xl font-bold text-red-600">{data?.xirr ?? 0.0}%</p>

          <div className="mt-1 flex items-center gap-1 text-xs text-red-600">
            <ArrowDownRight className="h-3 w-3" />
            <span className="text-muted-foreground">Since inception</span>
          </div>
        </div>

        {/* FY XIRR */}
        <div className="rounded-xl border border-green-200 bg-green-50 p-4">
          <div className="mb-2 flex items-center gap-2">
            <Calendar className="h-4 w-4 text-green-600" />
            <p className="text-xs text-muted-foreground">FY XIRR</p>
          </div>

          <p className="text-xl font-bold text-green-600">
            {data?.FYxirr ?? 0.0}%
          </p>
          <p className="mt-1 text-xs text-muted-foreground">Current FY</p>
        </div>

        {/* Current Value */}
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="mb-2 flex items-center gap-2">
            <Wallet className="h-4 w-4 text-blue-600" />
            <p className="text-xs text-muted-foreground">Current Value</p>
          </div>

          <p className="text-xl font-bold text-blue-600">
            ₹{data?.currentHoldingValuation ?? 0.0}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Holding valuation
          </p>
        </div>

        {/* Total Contributed */}
        <div className="rounded-xl border border-border/60 bg-muted/30 p-4">
          <div className="mb-2 flex items-center gap-2">
            <PersonStanding className="h-4 w-4 text-secondary" />
            <p className="text-xs text-muted-foreground">Total contribution</p>
          </div>

          <p className="text-xl font-bold text-secondary">
            {data?.totalNumberContribution ?? 0}
          </p>

          <p className="mt-1 text-xs text-muted-foreground">contribution(s)</p>
        </div>

        {/* Notional Gain/Loss */}
        {/* <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="mb-2 flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-red-600" />
            <p className="text-xs text-muted-foreground">Notional Gain/Loss</p>
          </div>

          <p className="text-xl font-bold text-red-600">
            -₹{data?.notionalGainLoss ?? 0.0}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">Unrealized</p>
        </div> */}

        {/* Total Withdrawal */}
        <div className="rounded-xl border border-border/60 bg-muted/30 p-4">
          <div className="mb-2 flex items-center gap-2">
            <IndianRupee className="h-4 w-4 text-amber-600" />
            <p className="text-xs text-muted-foreground">Total Withdrawal</p>
          </div>

          <p className="text-xl font-bold text-secondary">
            ₹{data?.totalWithdrawal ?? 0.0}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">Redeemed amount</p>
        </div>
      </div>

      {/* Info Note */}
      <div className="mt-4 flex items-start gap-2 rounded-lg bg-blue-50 p-3">
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
        <p className="text-sm text-blue-700">
          XIRR (Extended Internal Rate of Return) is the annualized return
          considering the timing and amount of each contribution. Notional
          gain/loss represents unrealized profit/loss on your current holdings.
        </p>
      </div>
    </div>
  );
};

export default XirrPerformance;
